/* global ymaps3 */
import getjs from 'get-js';
import { throttle, debounce } from 'throttle-debounce';

import map from 'lodash/map';
import max from 'lodash/max';
import min from 'lodash/min';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

import { t } from '@/helpers/i18n';
import { isMobile } from '@/helpers/mobile_detect';

const DEBOUNCE_INTERVAL = 150;
export const MAP_ANIMATION_INTERVAL = 500;

const ZOOM_RANGE = { min: 4, max: 17 };
const MARGINS = [60, 120, 90, 60];

export async function loadMapLibrary() {
  if (!('ymaps3' in window)) {
    const YANDEX_MAP_API_URL = t('frontend.map.apiUrl');
    await getjs(YANDEX_MAP_API_URL);
  }
  await ymaps3.ready;
  const { default: Mark } = await import('./mark');

  return { Mark };
}

export async function buildMap({
  node,
  geo,
  marksData,
  leadProps,
  isAutoCenter = true,
  isAutoOpen = false,
  isVueDataFlow = false,
  isNoBalloon = false,
  callbacks = {
    boundsChange: (_visibleMarks, _removedMarks) => {},
    showBalloon: null,
    hideBalloon: null
  }
}) {
  const { Mark } = await loadMapLibrary();
  const { YMapZoomControl, YMapGeolocationControl } = await ymaps3.import('@yandex/ymaps3-controls@0.0.1');

  // https://yandex.ru/dev/jsapi30/doc/ru/ref/
  const ymap = new ymaps3.YMap(node, {
    location: {
      center: [geo.longitude, geo.latitude],
      zoom: geo.zoom
    },
    behaviors: [
      'drag', 'pinchZoom', 'dblClick', 'magnifier', 'oneFingerZoom', 'mouseRotate',
      'mouseTilt', 'pinchRotate', 'panTilt', 'scrollZoom'
    ],
    zoomRange: ZOOM_RANGE,
    showScaleInCopyrights: true
    // margin: MARGINS
  },
  [
    new ymaps3.YMapDefaultSchemeLayer({}),
    new ymaps3.YMapDefaultFeaturesLayer({})
  ]
  );

  // не добавляем контролы для мобильной версии с автооткрытием балуна
  if (!(isAutoOpen && isMobile())) {
    ymap.addChild(
      new ymaps3.YMapControls({ position: 'right' })
        .addChild(new YMapZoomControl({}))
        .addChild(new YMapGeolocationControl({}))
    );
  }

  // сортировка, чтобы маркеры, расположенные правее и ниже, отображались над
  // маркерами, расположенными левее и выше
  const marks = marksData
    |> sortBy(?, 'latitude', 'longitude')
    |> reverse(?)
    |> map(?, markData => (
      new Mark({
        markData,
        leadProps,
        ymap,
        isAutoOpen,
        isVueDataFlow,
        isNoBalloon,
        emitBalloonShow: isVueDataFlow && callbacks.showBalloon,
        emitBalloonHide: isVueDataFlow && callbacks.hideBalloon
      })
    ));

  marks.forEach(mark => ymap.addChild(mark.marker));

  if (isAutoCenter && marks.length) {
    fitMapToMarkers(ymap, marks, true);
  }

  ymap.addChild(new ymaps3.YMapListener({
    layer: 'any',
    onUpdate: debounce(DEBOUNCE_INTERVAL, () => {
      const [
        [longitudeFrom, latitudeTo],
        [longitudeTo, latitudeFrom]
      ] = ymap.bounds;
      // console.log('bounds', ymap.bounds);

      const visibleMarks = marks.filter(mark => (
        mark.longitude >= longitudeFrom && mark.longitude <= longitudeTo &&
          mark.latitude >= latitudeFrom && mark.latitude <= latitudeTo
      ));

      callbacks.boundsChange(visibleMarks);
    })
    // onClick: (_, { coordinates }) => console.log(coordinates)
  }));

  return { map: ymap, marks, fitMapToMarkers };
}

const fitMapToMarkers = (ymap, marks, isSkipAnimation) => {
  if (!marks.length) { return; }

  const latitudes = map(marks, 'latitude');
  const longitudes = map(marks, 'longitude');

  const latitudeFrom = min(latitudes);
  const latitudeTo = max(latitudes);

  const longitudeFrom = min(longitudes);
  const longitudeTo = max(longitudes);

  // const bounds = [
  //   [longitudeFrom, latitudeTo],
  //   [longitudeTo, latitudeFrom]
  // ];

  const latRange = latitudeTo - latitudeFrom;
  const lngRange = longitudeTo - longitudeFrom;

  const paddingFactor = 0.1; // 10% отступа
  const paddedBounds = [
    [longitudeFrom - lngRange * paddingFactor, latitudeTo + latRange * paddingFactor],
    [longitudeTo + lngRange * paddingFactor, latitudeFrom - latRange * paddingFactor]
  ];

  ymap.setLocation({
    bounds: paddedBounds,
    easing: 'ease',
    duration: isSkipAnimation ? 0 : MAP_ANIMATION_INTERVAL
  });
};
